import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { usePublicOrderNotifications } from "@/hooks/usePublicOrderNotifications";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useUserRole } from "@/hooks/useUserRole";
import { usePendingPrintJobsAlert } from "@/hooks/usePendingPrintJobsAlert";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { useRealtimeManager } from "@/hooks/useRealtimeManager";
import { useQZTrayConnection } from "@/hooks/useQZTrayConnection";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarErrorBoundary } from "@/components/SidebarErrorBoundary";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { Loader2, Menu } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { RealtimeIndicator } from "@/components/RealtimeIndicator";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { PublicOrderNotificationPanel } from "@/components/notifications/PublicOrderNotificationPanel";
import { WaiterCallNotificationPanel } from "@/components/notifications/WaiterCallNotificationPanel";
import { PushNotificationToggle } from "@/components/notifications/PushNotificationToggle";
import { PrinterStatusIndicator } from "@/components/printers/PrinterStatusIndicator";
import { VersionUpdateBanner } from "@/components/pwa/VersionUpdateBanner";
import { OfflineStartupScreen } from "@/components/OfflineStartupScreen";
import { saveBootstrapData, isOffline, isBootstrapValid } from "@/lib/offline-bootstrap";

// Breadcrumb helper
function getBreadcrumb(pathname: string): string {
  const routes: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/dashboard/categorias": "Categorias",
    "/dashboard/produtos": "Produtos",
    "/dashboard/pdv": "PDV",
    "/dashboard/pedidos": "Pedidos",
    "/dashboard/relatorios": "Relatórios",
    "/dashboard/configuracoes": "Configurações",
    "/dashboard/erros": "Log de Erros",
  };
  return routes[pathname] || "Dashboard";
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, isOfflineMode: authOffline } = useAuth();
  const { isLoading: restaurantLoading, needsOnboarding, activeRestaurant, isOfflineMode: restaurantOffline } = useRestaurantContext();
  const { isSuperAdmin, isLoading: superAdminLoading } = useSuperAdmin();
  const { isWaiter, isCashier, isLoading: roleLoading } = useUserRole();
  const { 
    newPublicOrders, 
    hasNewOrders, 
    clearNewOrders, 
    dismissOrder 
  } = usePublicOrderNotifications();

  const [isRetrying, setIsRetrying] = useState(false);

  // Monitor pending print jobs and alert if waiting too long
  usePendingPrintJobsAlert(true);

  // Version check for PWA updates
  const { isOutdated, serverVersion, localVersion, isDismissed, dismiss } = useVersionCheck();

  // Centralized Realtime channel manager - monitors and auto-reconnects channels
  useRealtimeManager();

  // Global QZ Tray connection monitor - keeps printer connected across all pages
  useQZTrayConnection();

  // Auto-save bootstrap data when online with valid session
  useEffect(() => {
    if (user && activeRestaurant && navigator.onLine && !authOffline && !restaurantOffline) {
      saveBootstrapData({
        userId: user.id,
        userEmail: user.email || '',
        userName: user.user_metadata?.full_name,
        restaurantId: activeRestaurant.id,
        restaurantName: activeRestaurant.name,
        restaurantSlug: activeRestaurant.slug,
      });
    }
  }, [user, activeRestaurant, authOffline, restaurantOffline]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user && !isOffline()) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Redirect super admins to /admin - they shouldn't use the restaurant dashboard
  useEffect(() => {
    if (!authLoading && !superAdminLoading && user && isSuperAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [authLoading, superAdminLoading, user, isSuperAdmin, navigate]);

  // Redirect waiters to /dashboard/garcom when accessing restricted pages
  useEffect(() => {
    if (!authLoading && !roleLoading && user && isWaiter) {
      const allowedPaths = ["/dashboard/garcom", "/dashboard/pdv"];
      const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));
      
      if (!isAllowedPath) {
        navigate("/dashboard/garcom", { replace: true });
      }
    }
  }, [authLoading, roleLoading, user, isWaiter, location.pathname, navigate]);

  // Redirect cashiers to /dashboard/pdv when accessing restricted pages
  useEffect(() => {
    if (!authLoading && !roleLoading && user && isCashier && !isWaiter) {
      const allowedPaths = [
        "/dashboard/pdv",
        "/dashboard/garcom",
        "/dashboard/abertura-comandas",
        "/dashboard/consumo-aberto",
        "/dashboard/caixa",
        "/dashboard/pedidos",
      ];
      const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));
      
      if (!isAllowedPath) {
        navigate("/dashboard/pdv", { replace: true });
      }
    }
  }, [authLoading, roleLoading, user, isCashier, isWaiter, location.pathname, navigate]);

  // Redirect to onboarding if no restaurant (but not for super admins and not in offline mode)
  useEffect(() => {
    if (!authLoading && user && !restaurantLoading && !superAdminLoading && needsOnboarding && !isSuperAdmin && !authOffline && !restaurantOffline) {
      navigate("/onboarding");
    }
  }, [authLoading, user, restaurantLoading, superAdminLoading, needsOnboarding, isSuperAdmin, authOffline, restaurantOffline, navigate]);

  // Handle retry for offline startup
  const handleRetry = async () => {
    setIsRetrying(true);
    // Wait a moment and reload
    await new Promise(resolve => setTimeout(resolve, 1000));
    window.location.reload();
  };

  // Show loading state
  if (authLoading || restaurantLoading || superAdminLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  // Show offline startup screen if offline without valid cache
  if (!user && isOffline() && !isBootstrapValid()) {
    return <OfflineStartupScreen onRetry={handleRetry} isRetrying={isRetrying} />;
  }

  if (!user || (needsOnboarding && !isSuperAdmin && !authOffline && !restaurantOffline)) {
    return null;
  }

  const breadcrumb = getBreadcrumb(location.pathname);
  const isPDV = location.pathname.startsWith("/dashboard/pdv");
  const isInOfflineMode = authOffline || restaurantOffline;

  return (
    <SidebarProvider>
      <div className="h-[100dvh] w-full flex flex-col overflow-hidden">
        {/* Version update banner */}
        {isOutdated && !isDismissed && serverVersion && (
          <div className="shrink-0">
            <VersionUpdateBanner
              serverVersion={serverVersion}
              localVersion={localVersion}
              onDismiss={dismiss}
            />
          </div>
        )}
        
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <SidebarErrorBoundary>
            <AppSidebar />
          </SidebarErrorBoundary>
          <SidebarInset className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Top Header */}
            <header className="h-14 shrink-0 flex items-center gap-2 sm:gap-4 border-b border-border bg-card px-2 sm:px-4 min-w-0 overflow-hidden">
              <SidebarTrigger className="-ml-1 shrink-0">
                <Menu className="h-5 w-5" />
              </SidebarTrigger>
              <Separator orientation="vertical" className="h-6 shrink-0 hidden sm:block" />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground truncate">{breadcrumb}</span>
                {isInOfflineMode && (
                  <span className="text-xs bg-amber-500/10 text-amber-700 px-2 py-0.5 rounded-full">
                    Offline
                  </span>
                )}
              </div>
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <PrinterStatusIndicator />
                <PushNotificationToggle />
              </div>
              <WaiterCallNotificationPanel />
              <PublicOrderNotificationPanel
                orders={newPublicOrders}
                hasNewOrders={hasNewOrders}
                onDismiss={dismissOrder}
                onClearAll={clearNewOrders}
              />
              <NotificationBell />
              <RealtimeIndicator />
            </header>

            {/* Page Content - overflow-hidden for PDV, overflow-auto for others */}
            <main className={`flex-1 min-h-0 bg-background ${isPDV ? "overflow-hidden" : "overflow-auto"}`}>
              <Outlet />
            </main>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}
